import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { landingAsset } from "../utils/landingAsset";
import CarrierGlobe from "../components/CarrierGlobe";
import "./LandingPages.scss";
import "./SolutionsLanding.scss";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const typeText = async (setter, text, signal, delay = 55) => {
  let acc = "";
  for (const ch of text) {
    if (signal.aborted) return;
    acc += ch;
    setter(acc);
    await wait(delay);
  }
};

const TRUCK_TYPES = ["Cargo", "Tanker", "Refrigerated"];

const SolutionsCarrier = () => {
  const [truck, setTruck] = useState("");
  const [truckOpen, setTruckOpen] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [location, setLocation] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [published, setPublished] = useState(false);
  const [pressing, setPressing] = useState(false);

  // Auto demo walkthrough (same idea as the product video)
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const runDemo = async () => {
      while (!signal.aborted) {
        setPublished(false);
        setPressing(false);
        setCapacity("");
        setLocation("");
        setFrom("");
        setTo("");
        setTruck("");
        setTruckOpen(false);
        await wait(900);
        if (signal.aborted) break;

        setTruckOpen(true);
        await wait(700);
        if (signal.aborted) break;

        setTruck("Tanker");
        await wait(350);
        setTruckOpen(false);
        await wait(450);
        if (signal.aborted) break;

        await typeText(setCapacity, "25,000kg", signal);
        await wait(450);
        if (signal.aborted) break;

        await typeText(setLocation, "Rotterdam", signal);
        await wait(450);
        if (signal.aborted) break;

        // Full dates appear at once (not digit-by-digit)
        setFrom("23/07/2026");
        await wait(500);
        if (signal.aborted) break;

        setTo("25/07/2026");
        await wait(1400);
        if (signal.aborted) break;

        setPressing(true);
        await wait(160);
        if (signal.aborted) break;
        setPublished(true);
        await wait(120);
        setPressing(false);
        await wait(2800);
      }
    };

    runDemo();
    return () => controller.abort();
  }, []);

  const stepTruck = !!truck;
  const stepCapacity = !!capacity.trim();
  const stepLocation = !!location.trim();
  const stepDates = from.length >= 10 && to.length >= 10;
  const stepsDone = [stepTruck, stepCapacity, stepLocation, stepDates].filter(
    Boolean
  ).length;
  const ready = stepsDone === 4;

  const publishClass = [
    "publish",
    (ready || published) && "is-published",
    pressing && "is-pressed",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="mtk-landing mtk-solutions-carrier">
      <section className="mtk-page-hero">
        <div className="hero-inner">
          <h1>
            One platform,
            <br />
            <span>more opportunities.</span>
          </h1>
        </div>
      </section>

      <section className="mtk-carrier-join">
        <div className="inner">
          <h2>
            Join our European carrier network{" "}
            <span className="free">For Free</span>
          </h2>
          <p className="lead">
            Get direct access to shippers looking for capacity across Europe. Fill the trucks you
            already run, cut empty kilometres on your return legs, and win new clients without
            prospecting. Quotes, orders, tracking and documents all run from one platform, so your
            team spends less time on the phone.
          </p>

          <div className="globe-wrap">
            <div className="globe">
              <CarrierGlobe size={400} />
            </div>
            <div className="benefit-grid">
              {[
                [
                  "Revenue growth",
                  "Reach new shippers and generate additional revenue through qualified transport demand.",
                ],
                [
                  "Optimised routes",
                  "Better fleet utilisation, fewer empty kilometres and more paid return journeys.",
                ],
                [
                  "Reduce communication time",
                  "Spend less time on calls and emails with a centralised platform for transport management.",
                ],
              ].map(([t, d]) => (
                <div key={t} className="benefit">
                  <h4>{t}</h4>
                  <p>{d}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="video-block">
            <div className="video-ph">
              <video
                className="carrier-video"
                src={landingAsset("carrier-network-clip.mp4")}
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                aria-label="Mottruck carrier network overview"
              />
            </div>

            <h3 className="avail-title">Turn your availability into loads</h3>
            <div className="avail-grid">
              <div className="avail-card">
                <div className="avail-steps" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className={`avail-step${i < stepsDone ? " done" : ""}`}
                    />
                  ))}
                </div>

                <div className="avail-head">Set your availability</div>

                <div className={`row${stepTruck || truckOpen ? " active" : ""}`}>
                  <div className="lab">Truck type</div>
                  <div className={`val truck-dd${truckOpen ? " open" : ""}`}>
                    <div className="dd-trigger">
                      <span className={truck ? "" : "ph"}>
                        {truck || "Select type"}
                      </span>
                      <span className="caret" aria-hidden="true" />
                    </div>
                    {truckOpen ? (
                      <div className="dd-menu" role="listbox">
                        {TRUCK_TYPES.map((label) => (
                          <div
                            key={label}
                            className={`dd-option${truck === label ? " on" : ""}`}
                            role="option"
                            aria-selected={truck === label}
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className={`row${stepCapacity ? " active" : ""}`}>
                  <div className="lab">Capacity</div>
                  <div className="val">
                    <input
                      placeholder="e.g. 25,000kg"
                      value={capacity}
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                </div>

                <div className={`row${stepLocation ? " active" : ""}`}>
                  <div className="lab">Location</div>
                  <div className="val">
                    <input
                      placeholder="e.g. Rotterdam"
                      value={location}
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                </div>

                <div className={`row${stepDates ? " active" : ""}`}>
                  <div className="lab">Dates</div>
                  <div className="val dates">
                    <input
                      type="text"
                      placeholder="dd/mm/yyyy"
                      className={from ? "filled" : ""}
                      value={from}
                      readOnly
                      tabIndex={-1}
                    />
                    <span className="date-arrow" aria-hidden="true">
                      →
                    </span>
                    <input
                      type="text"
                      placeholder="dd/mm/yyyy"
                      className={to ? "filled" : ""}
                      value={to}
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                </div>

                <button type="button" className={publishClass} tabIndex={-1}>
                  Publish availability
                </button>
                <p className={`publish-hint${published ? " is-on" : ""}`}>
                  ✓ You&apos;re now visible to shippers searching this route
                </p>
              </div>
              <p className="avail-copy">
                Declare your truck availability in a few seconds: truck type, capacity, location and
                free dates. Your trucks then appear to shippers looking for capacity on those lanes,
                and price requests reach you directly. Update or publish a new availability whenever
                your planning changes, so your fleet stays visible and your empty kilometres go down.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mtk-built-carriers">
        <div className="mtk-wrap grid">
          <div className="copy">
            <h2>Built for carriers</h2>
            <p>
              Quotes, orders, availabilities, tracking, claims and eDocuments in one place. Answer
              price requests in a few clicks and keep every order in a single interface. Our mobile
              application collects transport documents on delivery.
            </p>
          </div>
          <div className="photo">
            <img
              src={landingAsset("mottruck-driver-app.jpg")}
              alt="Mottruck driver app — Designed for drivers effectiveness"
            />
          </div>
        </div>
      </section>

      <section className="mtk-expectations">
        <div className="mtk-wrap-1240">
          <h2>Meet shippers&apos; expectations</h2>
          <p className="sub">The platform helps you deliver on each of them.</p>
          <div className="exp-grid">
            <div
              className="photo"
              style={{
                backgroundImage: `url(${landingAsset("shipper-expectations.webp")})`,
              }}
            />
            <div className="tiles">
              {[
                [
                  "Reliable capacity",
                  "Trucks confirmed on the agreed dates, with no last-minute cancellations.",
                ],
                [
                  "Clear pricing",
                  "A quote answered quickly, with rates they can compare and understand.",
                ],
                [
                  "Visibility on delivery",
                  "Order statuses updated at each step, without having to call for news.",
                ],
                [
                  "Documents on time",
                  "Proofs of delivery and order confirmations sent back without delay.",
                ],
              ].map(([t, d]) => (
                <div key={t} className="tile">
                  <h4>{t}</h4>
                  <p>{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mtk-carrier-benefits">
        <div className="mtk-wrap-1240">
          <div className="label">Benefits</div>
          <div className="cols">
            {[
              [
                "Ready for the future of transport",
                "Join a digital ecosystem built for modern logistics, helping you to adapt to changing shipper expectations and stay competitive in a rapidly evolving market.",
              ],
              [
                "A simpler way to work with shippers",
                "Work more efficiently with shippers through a platform designed to simplify day-to-day transport management.",
              ],
              [
                "New opportunities, on your terms",
                "Access relevant transport opportunities and choose the ones that best fit your capabilities and business objectives.",
              ],
            ].map(([t, d]) => (
              <div key={t} className="col">
                <span className="dot" />
                <h4>{t}</h4>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mtk-carrier-how">
        <div className="mtk-wrap">
          <h2>How it works</h2>
          <div className="chevrons">
            {[
              [
                "01",
                "Create your carrier account",
                "Register your company, your fleet and your contacts. Setup takes a few minutes and costs nothing.",
              ],
              [
                "02",
                "Enter your truck availability",
                "Declare your service areas, capacity and free periods so shippers can find you on the lanes you run.",
              ],
              [
                "03",
                "Receive quote requests",
                "Price requests arrive in one place. Answer with your rate or decline the mission in a few clicks.",
              ],
              [
                "04",
                "Confirm the order to your driver",
                "Once the shipper validates your price, the order is confirmed and passed on to your driver.",
              ],
              [
                "05",
                "Driver scans the e-documents",
                "On delivery, your driver scans the transport documents from the mobile app. No paperwork chase afterwards.",
              ],
            ].map(([n, t, d]) => (
              <div key={n} className="chevron">
                <span className="n">{n}</span>
                <div>
                  <div className="t">{t}</div>
                  <div className="d">{d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="how-cta">
            <Link to="/join">Join our network for free →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsCarrier;
